/**
 * @file: extendedPropertyUtil.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  ExtendedPropertyValidationErrorInfo,
  OrderExtra,
} from '@euler/app/flows/order/types';
import { ExtendedProperty, StoreConfiguration } from '@euler/model';
import { array2map } from '@euler/utils/array';
import { isExtendedPropertyTypeSupported } from './isExtendedPropertyTypeSupported';

export const kDefaultDatetimeFormat = 'yyyy-MM-dd HH:mm:ss';
export const kDefaultDateFormat = 'yyyy-MM-dd';

export function normalizedExtendedPropertiesFromOrderExtra(
  detail: OrderExtra,
  storeConfiguration: StoreConfiguration,
) {
  const extendedOrderProperties =
    storeConfiguration.extendedOrderProperties?.filter(x =>
      isExtendedPropertyTypeSupported(x.type),
    );
  const propertyDefMap = array2map(extendedOrderProperties ?? [], x => x.id);
  const propertyIdSet = new Set(propertyDefMap.keys());

  const normalizedValue = (id: string, value: any) => {
    const def = propertyDefMap.get(id)!;
    if (value == null) return value;
    if (def.type === 'text') {
      return typeof value === 'string' ? value.trim() : String(value);
    } else if (def.type === 'switch') {
      return Boolean(value);
    } else if (def.type === 'select') {
      if (def.multi) {
        if (Array.isArray(value)) {
          return value.filter(x => x);
        }
        return value ? [value] : [];
      } else {
        if (Array.isArray(value)) {
          return value[0];
        }
        return value;
      }
    } else if (def.type === 'datetime') {
      return typeof value === 'string'
        ? value
        : value instanceof Date
        ? value.toISOString()
        : undefined;
    } else if (def.type === 'assets') {
      return Array.isArray(value)
        ? value
        : typeof value === 'string'
        ? [value]
        : [];
    }
  };
  const extendedProps =
    detail.extendedProps
      ?.filter(x => propertyIdSet.has(x.id))
      ?.map(p => ({
        ...p,
        value: normalizedValue(p.id, p.value),
      })) ?? [];
  const presentedPropertyIdSet = new Set(extendedProps.map(x => x.id));
  for (const propertyDef of extendedOrderProperties ?? []) {
    if (
      propertyDef.type === 'switch' &&
      !presentedPropertyIdSet.has(propertyDef.id)
    ) {
      // include all boolean properties if user don't even interact with
      extendedProps.push({
        id: propertyDef.id,
        value: propertyDef.defaultOn ?? false,
      });
    }
  }

  // sort the properties
  const map = array2map(extendedProps, x => x.id);
  const result: ExtendedProperty[] = [];
  for (const propertyDef of extendedOrderProperties ?? []) {
    if (map.has(propertyDef.id)) {
      result.push(map.get(propertyDef.id)!);
    }
  }

  return result.length > 0 ? result : undefined;
}

export function validateExtendedProperties(
  detail: OrderExtra,
  storeConfiguration: StoreConfiguration,
): ExtendedPropertyValidationErrorInfo | undefined {
  const properties = normalizedExtendedPropertiesFromOrderExtra(
    detail,
    storeConfiguration,
  );
  const extendedOrderProperties =
    storeConfiguration.extendedOrderProperties?.filter(x =>
      isExtendedPropertyTypeSupported(x.type),
    );

  let errorInfo: ExtendedPropertyValidationErrorInfo | undefined = undefined;
  const addError = (key: string, msg: string) => {
    if (errorInfo == null) {
      errorInfo = new ExtendedPropertyValidationErrorInfo();
    }
    errorInfo.errors[key] = msg;
  };

  const propertyMap = array2map(properties ?? [], x => x.id);

  for (const propertyDef of extendedOrderProperties ?? []) {
    if (!propertyDef.required) continue;

    const property = propertyMap.get(propertyDef.id);

    let hasValue: boolean = false;

    if (property?.value != null) {
      const isNotEmptyString =
        typeof property.value === 'string' && property.value.length > 0;
      if (propertyDef.type === 'text' || propertyDef.type === 'datetime') {
        hasValue = isNotEmptyString;
      } else if (propertyDef.type === 'select') {
        if (propertyDef.multi) {
          hasValue = Array.isArray(property.value) && property.value.length > 0;
        } else {
          hasValue = isNotEmptyString;
        }
      } else if (propertyDef.type === 'switch') {
        hasValue = typeof property.value === 'boolean';
      } else if (propertyDef.type === 'assets') {
        hasValue = Array.isArray(property.value) && property.value.length > 0;
      }
    }

    if (!hasValue) {
      addError(
        propertyDef.id,
        __DEV__ ? `${propertyDef.name}不能为空` : `该字段不能为空`,
      );
    }
  }

  return errorInfo;
}
